/* Ambient Three.js background for the demo.
 *
 * Simplified from the v1 version: the foreground content is now an HTML
 * card (see screens.ts), so the 3D's job is atmosphere — a soft particle
 * field with hue shifts per phase. No more orbiting planets / pricing
 * pillars in 3D; those live in the HTML mocks where typography is legible.
 */

import * as THREE from 'three';
import type { SceneKey } from './phases';

const COLORS: Record<SceneKey, [THREE.Color, THREE.Color]> = {
  cool:   [new THREE.Color(0x7fffd4), new THREE.Color(0x6b8af5)],
  warm:   [new THREE.Color(0xf38281), new THREE.Color(0xfce28a)],
  matrix: [new THREE.Color(0x00ff9c), new THREE.Color(0x7fffd4)],
  gold:   [new THREE.Color(0xfce28a), new THREE.Color(0xf7b285)],
  coral:  [new THREE.Color(0xf38281), new THREE.Color(0xff7a37)],
};

export class Scene {
  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private particles: THREE.Points;
  private particleGeom: THREE.BufferGeometry;
  private velocities: Float32Array;
  private raf = 0;
  private clock = new THREE.Clock();
  private paused = false;
  private currentPrimary: THREE.Color;
  private currentAccent: THREE.Color;
  private targetPrimary: THREE.Color;
  private targetAccent: THREE.Color;

  constructor(canvas: HTMLCanvasElement) {
    const w = window.innerWidth;
    const h = window.innerHeight;

    this.renderer = new THREE.WebGLRenderer({
      canvas, alpha: true, antialias: window.devicePixelRatio < 2,
      powerPreference: 'high-performance',
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.renderer.setSize(w, h, false);
    this.renderer.setClearColor(0x000000, 0);

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(58, w / h, 0.1, 100);
    this.camera.position.set(0, 0, 6);
    this.camera.lookAt(0, 0, 0);

    const PCOUNT = 1400;
    const positions = new Float32Array(PCOUNT * 3);
    const colors = new Float32Array(PCOUNT * 3);
    this.velocities = new Float32Array(PCOUNT * 3);
    const [p0, a0] = COLORS.matrix;
    this.currentPrimary = p0.clone();
    this.currentAccent = a0.clone();
    this.targetPrimary = p0.clone();
    this.targetAccent = a0.clone();

    for (let i = 0; i < PCOUNT; i++) {
      const r = 3 + Math.random() * 7;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      positions[i * 3 + 0] = Math.sin(phi) * Math.cos(theta) * r;
      positions[i * 3 + 1] = Math.sin(phi) * Math.sin(theta) * r * 0.7;
      positions[i * 3 + 2] = Math.cos(phi) * r * 0.7;
      const mix = Math.random();
      const c = p0.clone().lerp(a0, mix);
      colors[i * 3 + 0] = c.r;
      colors[i * 3 + 1] = c.g;
      colors[i * 3 + 2] = c.b;
      this.velocities[i * 3 + 0] = (Math.random() - 0.5) * 0.001;
      this.velocities[i * 3 + 1] = (Math.random() - 0.5) * 0.001;
      this.velocities[i * 3 + 2] = (Math.random() - 0.5) * 0.001;
    }
    this.particleGeom = new THREE.BufferGeometry();
    this.particleGeom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this.particleGeom.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    const pmat = new THREE.PointsMaterial({
      size: 0.05,
      vertexColors: true,
      transparent: true,
      opacity: 0.55,  // intentionally subtle — ambient, not focal
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      sizeAttenuation: true,
    });
    this.particles = new THREE.Points(this.particleGeom, pmat);
    this.scene.add(this.particles);

    window.addEventListener('resize', this.onResize);
  }

  setPaused(p: boolean): void { this.paused = p; }

  setScene(key: SceneKey): void {
    const [p, a] = COLORS[key] || COLORS.matrix;
    this.targetPrimary = p;
    this.targetAccent = a;
  }

  start(): void {
    this.clock.start();
    const loop = () => {
      this.raf = requestAnimationFrame(loop);
      if (this.paused) { this.renderer.render(this.scene, this.camera); return; }
      this.tick();
      this.renderer.render(this.scene, this.camera);
    };
    loop();
  }

  stop(): void {
    if (this.raf) cancelAnimationFrame(this.raf);
    window.removeEventListener('resize', this.onResize);
    this.renderer.dispose();
  }

  private tick(): void {
    const dt = this.clock.getDelta();
    const positions = this.particleGeom.getAttribute('position').array as Float32Array;
    const colors = this.particleGeom.getAttribute('color').array as Float32Array;
    const PCOUNT = positions.length / 3;
    for (let i = 0; i < PCOUNT; i++) {
      positions[i * 3 + 0] += this.velocities[i * 3 + 0];
      positions[i * 3 + 1] += this.velocities[i * 3 + 1];
      positions[i * 3 + 2] += this.velocities[i * 3 + 2];
      const x = positions[i * 3 + 0];
      const y = positions[i * 3 + 1];
      const z = positions[i * 3 + 2];
      const r = Math.sqrt(x*x + y*y + z*z);
      if (r > 10 || r < 2) {
        const scale = 5 / Math.max(0.001, r);
        positions[i * 3 + 0] *= scale;
        positions[i * 3 + 1] *= scale;
        positions[i * 3 + 2] *= scale;
      }
    }
    (this.particleGeom.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;

    this.currentPrimary.lerp(this.targetPrimary, dt * 0.8);
    this.currentAccent.lerp(this.targetAccent, dt * 0.8);
    const tint = 50;
    for (let n = 0; n < tint; n++) {
      const i = Math.floor(Math.random() * PCOUNT);
      const mix = Math.random();
      const c = this.currentPrimary.clone().lerp(this.currentAccent, mix);
      colors[i * 3 + 0] += (c.r - colors[i * 3 + 0]) * 0.08;
      colors[i * 3 + 1] += (c.g - colors[i * 3 + 1]) * 0.08;
      colors[i * 3 + 2] += (c.b - colors[i * 3 + 2]) * 0.08;
    }
    (this.particleGeom.getAttribute('color') as THREE.BufferAttribute).needsUpdate = true;

    this.particles.rotation.y -= dt * 0.015;
    this.particles.rotation.x += dt * 0.005;
  }

  private onResize = (): void => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h, false);
  };
}
